import {
  Box,
  FormControl,
  FormControlLabel,
  InputAdornment,
  Radio,
  RadioGroup,
  type RadioProps,
  TextField,
  Typography,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { type FC, useCallback, useEffect, useState } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { fromISO8601_DATE } from "../../../utils/iso8601";
import type { CustomRepeatEnd } from "../hooks/use-number-of-repeats";
import type { Event } from "../types";

const RadioWithAnalyticsId: FC<RadioProps & { analyticsId: string }> = ({
  analyticsId,
  ...rest
}) => (
  <Radio
    {...rest}
    slotProps={{
      input: { "data-analytics-id": analyticsId, ...rest.slotProps?.input },
      ...rest.slotProps,
    }}
  />
);

export const CustomRepeatEnds: FC<{
  disabled: boolean;
  onChange: (end: CustomRepeatEnd) => void;
}> = ({ disabled, onChange }) => {
  const [state, setState] = useState<{
    endType: CustomRepeatEnd;
    endDate: Date;
    occurrences: number;
  }>({
    endType: { type: "never" },
    endDate: new Date(),
    occurrences: 10,
  });
  const { control, getValues } = useFormContext<Event>();

  useWatch({ control, name: "date" });
  const date = getValues("date");

  const handleRadioChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const endType = event.target.value as CustomRepeatEnd["type"];

    if (endType === "onDate") {
      return setState((prev) => ({
        ...prev,
        endType: { type: "onDate", date: prev.endDate },
      }));
    }

    if (endType === "after") {
      return setState((prev) => ({
        ...prev,
        endType: { type: "after", occurrences: prev.occurrences },
      }));
    }

    if (endType === "never") {
      return setState((prev) => ({
        ...prev,
        endType: { type: "never" },
      }));
    }
  }, []);

  useEffect(() => onChange(state.endType), [state.endType, onChange]);

  return (
    <Box display="flex" alignItems="center" gap={2}>
      <Typography variant="body1" sx={{ minWidth: 40 }}>
        Ends:
      </Typography>

      <FormControl component="fieldset" disabled={disabled}>
        <RadioGroup row value={state.endType.type} onChange={handleRadioChange}>
          <FormControlLabel
            value="never"
            control={
              <RadioWithAnalyticsId analyticsId="create-event-form-custom-repeat-ends-never" />
            }
            label="Never"
          />

          <FormControlLabel
            value="onDate"
            control={
              <RadioWithAnalyticsId analyticsId="create-event-form-custom-repeat-ends-date" />
            }
            label={
              <Box display="flex" alignItems="center" gap={1}>
                <span>On</span>
                <DatePicker
                  value={state.endDate}
                  onChange={(newValue) => {
                    setState((prev) => ({
                      ...prev,
                      endDate: newValue ?? prev.endDate,
                      endType:
                        prev.endType.type === "onDate"
                          ? { type: "onDate", date: newValue ?? prev.endDate }
                          : prev.endType,
                    }));
                  }}
                  disabled={state.endType.type !== "onDate"}
                  slotProps={{
                    textField: {
                      size: "small",
                      inputProps: {
                        "data-analytics-id": "create-event-form-custom-repeat-ends-date-input",
                      },
                    },
                  }}
                  minDate={fromISO8601_DATE(date)}
                />
              </Box>
            }
          />

          <FormControlLabel
            value="after"
            control={
              <RadioWithAnalyticsId analyticsId="create-event-form-custom-repeat-ends-occurrences" />
            }
            label={
              <Box display="flex" alignItems="center" gap={1}>
                <span>After</span>
                <TextField
                  type="number"
                  size="small"
                  value={state.occurrences}
                  onChange={(e) => {
                    const occurrences = parseInt(e.target.value, 10);

                    setState((prev) => ({
                      ...prev,
                      occurrences,
                      endType:
                        prev.endType.type === "after"
                          ? { type: "after", occurrences }
                          : prev.endType,
                    }));
                  }}
                  disabled={state.endType.type !== "after"}
                  sx={{ maxWidth: 190 }}
                  slotProps={{
                    input: {
                      endAdornment: <InputAdornment position="end">occurrences</InputAdornment>,
                      inputProps: {
                        min: 1,
                        "data-analytics-id":
                          "create-event-form-custom-repeat-ends-occurrences-input",
                      },
                    },
                  }}
                />
              </Box>
            }
          />
        </RadioGroup>
      </FormControl>
    </Box>
  );
};
